import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta, { getOrganizationSchema, getWebSiteSchema, getFAQPageSchema } from '@/components/feature/SEOMeta';
import { bannerSlides, categories, hotVideos, latestUpdates, faqList } from '@/mocks/data';

function BannerCarousel() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % bannerSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const slide = bannerSlides[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-label="热门推荐轮播">
      <div
        className="absolute inset-0 bg-cover bg-center transition-all duration-700 ease-in-out"
        style={{ backgroundImage: `url(${slide.image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="max-w-lg md:max-w-xl">
              <span className={`inline-block ${slide.badgeBg} text-white text-xs font-medium px-3 py-1 rounded-full mb-3 md:mb-4 whitespace-nowrap`}>
                {slide.badge}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl text-white mb-2 md:mb-3 leading-tight">
                {slide.title}
              </h1>
              <p className="text-sm md:text-base text-white/80 mb-1 md:mb-2">
                {slide.subtitle}
              </p>
              <p className="text-xs md:text-sm text-white/60 mb-4 md:mb-6 line-clamp-2">
                {slide.description}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-fill text-yellow-400 text-sm"></i>
                  </span>
                  <span className="text-white font-semibold text-sm">{slide.rating}</span>
                </div>
                <Link
                  to={slide.link}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  立即观看
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {bannerSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'bg-white w-5 md:w-6' : 'bg-white/40 hover:bg-white/70'
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + bannerSlides.length) % bannerSlides.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={() => goTo((current + 1) % bannerSlides.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="w-full bg-background-50 py-6 md:py-8" aria-label="视频分类导航">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={cat.id === 'all' ? '/' : `/category/${cat.id}`}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                cat.id === 'all'
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon} text-sm`}></i>
              </span>
              {cat.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotVideos() {
  return (
    <section className="w-full py-10 md:py-14" aria-label="热门推荐视频">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
              热门推荐
            </h2>
            <p className="text-sm text-foreground-600 mt-1">全网最热影视资源，全部免费观看</p>
          </div>
          <Link
            to="/category/all"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            查看全部
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </span>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-product-shop="">
          {hotVideos.map((video) => (
            <Link
              key={video.id}
              to={video.link}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="group cursor-pointer"
            >
              <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2.5 md:mb-3">
                <img
                  src={video.image}
                  alt={`${video.title} - 芒果视频免费在线观看`}
                  title={`${video.title} - 芒果视频免费在线观看`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-2 left-2">
                  <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                    {video.badge}
                  </span>
                </div>
                <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-xs bg-black/50 px-2 py-1 rounded whitespace-nowrap">
                    {video.updateTo}
                  </span>
                </div>
                <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 text-yellow-400 text-xs px-1.5 py-0.5 rounded">
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-star-fill text-[10px]"></i>
                  </span>
                  {video.rating}
                </div>
              </div>
              <h3 className="text-sm md:text-base font-medium text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                {video.title}
              </h3>
              <p className="text-xs text-foreground-500 mt-0.5">{video.category} · {video.updateTo}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestUpdates() {
  return (
    <section className="w-full py-10 md:py-14 bg-background-100" aria-label="最新更新">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
          <div className="lg:w-2/3">
            <div className="mb-6 md:mb-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                最新更新
              </h2>
              <p className="text-sm text-foreground-600 mt-1">实时同步全网最新影视资源</p>
            </div>
            <div className="space-y-0">
              {latestUpdates.map((item, index) => (
                <Link
                  key={item.id}
                  to={item.link}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className={`flex items-center justify-between py-3 md:py-3.5 px-3 md:px-4 rounded-lg transition-all duration-200 cursor-pointer group ${
                    index % 2 === 0 ? 'bg-background-50' : 'bg-transparent'
                  } hover:bg-background-200`}
                >
                  <div className="flex items-center gap-3 md:gap-4 min-w-0">
                    <span className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm md:text-base font-medium text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 truncate">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        <span className="bg-accent-100 text-accent-700 text-xs px-1.5 py-0.5 rounded mr-2 whitespace-nowrap">{item.category}</span>
                        {item.episode}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-3 whitespace-nowrap">{item.time}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="lg:w-1/3">
            <div className="bg-background-50 rounded-xl p-5 md:p-6 sticky top-20">
              <h3 className="font-heading text-lg text-foreground-950 mb-4">
                热门标签
              </h3>
              <div className="flex flex-wrap gap-2">
                {['免费观看', '高清电影', '热门电视剧', '综艺节目', '国产动漫', '最新更新', '西瓜视频替代', '91视频替代', '不收费', '全集在线'].map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-all duration-200 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-background-200">
                <h3 className="font-heading text-lg text-foreground-950 mb-4">
                  为什么选择芒果视频？
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">完全免费</p>
                      <p className="text-xs text-foreground-500">无需付费，所有内容免费看</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">资源海量</p>
                      <p className="text-xs text-foreground-500">10万+影视资源，每日更新</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">高清流畅</p>
                      <p className="text-xs text-foreground-500">1080P高清画质，播放不缓冲</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">无需注册</p>
                      <p className="text-xs text-foreground-500">免注册直接看，方便快捷</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <section className="w-full py-10 md:py-14 bg-background-50" aria-label="常见问题" id="faq-section">
      <div className="max-w-[800px] mx-auto px-4 md:px-6">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
            常见问题
          </h2>
          <p className="text-sm text-foreground-600 mt-2">关于芒果视频，您想了解的一切</p>
        </div>

        <div className="space-y-3">
          {faqList.map((faq) => (
            <div
              key={faq.id}
              className="bg-background-100 rounded-xl overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
              >
                <span className="text-sm md:text-base font-medium text-foreground-950 pr-4">
                  {faq.question}
                </span>
                <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openId === faq.id ? 'rotate-45' : ''}`}>
                  <i className="ri-add-line text-foreground-500"></i>
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openId === faq.id ? 'max-h-96 pb-4 px-5' : 'max-h-0'
                }`}
              >
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="w-full py-10 md:py-14 relative overflow-hidden" aria-label="行动号召">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(https://readdy.ai/api/search-image?query=Warm%20cinematic%20gradient%20background%20with%20soft%20golden%20and%20amber%20light%20rays%2C%20abstract%20artistic%20style%2C%20elegant%20theater%20atmosphere%2C%20rich%20warm%20color%20palette%2C%20subtle%20bokeh%20effects&width=1920&height=400&seq=cta-bg-01&orientation=landscape)`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-accent-600/90" />
      <div className="relative max-w-[800px] mx-auto px-4 md:px-6 text-center">
        <h2 className="font-heading text-xl md:text-2xl text-white mb-3">
          立即开始免费观影
        </h2>
        <p className="text-sm md:text-base text-white/80 mb-6 max-w-lg mx-auto">
          海量影视资源，高清流畅播放，全部免费不收费。西瓜视频免费和91视频的最佳替代平台。
        </p>
        <Link
          to="/category/movie"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center gap-2 bg-white text-primary-600 px-6 py-3 rounded-full text-sm font-semibold hover:bg-white/90 transition-all duration-200 cursor-pointer whitespace-nowrap"
        >
          开始探索
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm"></i>
          </span>
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEOMeta
        title="芒果视频 | 西瓜视频免费与91视频在线观看替代 | 芒果视频免费播放不收费全集"
        description="芒果视频免费在线观看电影电视剧全集，替代西瓜视频免费、91视频等平台。无需付费即可享受海量影视资源，免费播放不收费，高清流畅不缓冲。"
        keywords="芒果视频,芒果视频免费,芒果视频在线观看,芒果视频免费播放,芒果视频不收费,西瓜视频免费,91视频,免费视频网站91,91在线视频,免费在线观看,电视剧全集"
        canonicalUrl="/"
        schema={getWebSiteSchema()}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <article>
            <BannerCarousel />
            <CategoryNav />
            <HotVideos />
            <LatestUpdates />
            <FAQ />
            <CTA />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}